/**
 * Web Push subscription utility.
 * Call initPush() after user logs in to register the service worker
 * and subscribe to push notifications.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export async function initPush(apiClient) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // Get VAPID public key
    const res = await apiClient.get('/push/vapid-public-key');
    if (!res.data.configured || !res.data.public_key) return;

    const applicationServerKey = urlBase64ToUint8Array(res.data.public_key);
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      // Already subscribed — just ensure backend has it
      await apiClient.post('/push/subscribe', existing.toJSON());
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    await apiClient.post('/push/subscribe', subscription.toJSON());
  } catch (e) {
    console.warn('[push] init failed:', e);
  }
}

export async function unsubscribePush(apiClient) {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await apiClient.delete('/push/unsubscribe', { data: { endpoint: sub.endpoint } });
      await sub.unsubscribe();
    }
  } catch (e) {
    console.warn('[push] unsubscribe failed:', e);
  }
}

export async function isPushEnabled() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub && Notification.permission === 'granted';
  } catch {
    return false;
  }
}
